// API functions for events and event sub-resources.
// Events use a flat route structure (not nested under categories).
// Permission filtering is done server-side via event category grants.

import type { ApiClient } from './client'

// --- Types ---

/** Event list response (summary fields). */
export interface Event {
  id: string
  title: string
  description?: string | null
  lifecycle_state: 'draft' | 'active'
  event_type_id: string
  event_type_name: string
  category_id: string
  category_name: string
  event_date?: string | null
  short_code: string
  recorded_by: string
  recorded_by_name: string
  recorded_at: string
  linked_risk_count: number
  linked_action_count: number
}

/** Event detail response (extends list with additional fields). */
export interface EventDetail extends Event {
  created_via: string
}

/** Event type (tenant-level configuration). */
export interface EventType {
  id: string
  type_name: string
  is_system_default: boolean
  created_at: string
}

/** Linked risk summary (read-only, from GET /events/:id/risks). */
export interface LinkedEventRisk {
  link_id: string
  risk_id: string
  risk_name: string
  register_id: string
  register_name: string
  lifecycle_state: string
  linked_at: string
}

/** Linked action summary (read-only, from GET /events/:id/actions). */
export interface LinkedEventAction {
  link_id: string
  action_id: string
  action_title: string
  lifecycle_state: string
  due_date?: string | null
  is_overdue: boolean
  linked_at: string
}

/** Evidence attached to an event. */
export interface EventEvidence {
  id: string
  file_id: string
  file_name: string
  file_size_bytes: number
  mime_type: string
  download_url: string
  evidence_tag_id: string
  tag_name: string
  comment?: string | null
  is_archived: boolean
  archived_by?: string | null
  archived_at?: string | null
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
}

// --- Request types ---

export interface CreateEventRequest {
  title: string
  description?: string
  event_type_id: string
  category_id: string
  event_date?: string
}

export interface UpdateEventRequest {
  title?: string
  description?: string
  event_type_id?: string
  event_date?: string
}

// CreateEvidenceRequest removed — evidence upload now uses multipart form via uploadEvidence().

// --- List filters ---

export interface EventListFilter {
  category_id?: string
  search?: string
  event_type_id?: string
  from_date?: string
  to_date?: string
}

// --- Lifecycle state helpers ---

export const eventLifecycleStates = ['draft', 'active'] as const

export const eventLifecycleStateLabels: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
}

export const eventLifecycleStateColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
}

// --- API functions ---

function buildFilterParams(filter?: EventListFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.category_id) params.set('category_id', filter.category_id)
  if (filter.search) params.set('search', filter.search)
  if (filter.event_type_id) params.set('event_type_id', filter.event_type_id)
  if (filter.from_date) params.set('from_date', filter.from_date)
  if (filter.to_date) params.set('to_date', filter.to_date)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function eventsApi(api: ApiClient) {
  return {
    // Event CRUD
    list: (filter?: EventListFilter) =>
      api.get<Event[]>(`/events${buildFilterParams(filter)}`),
    create: (data: CreateEventRequest) => api.post<EventDetail>('/events', data),
    get: (eventId: string) => api.get<EventDetail>(`/events/${eventId}`),
    update: (eventId: string, data: UpdateEventRequest) =>
      api.put<EventDetail>(`/events/${eventId}`, data),

    // State transitions
    activate: (eventId: string) =>
      api.post<EventDetail>(`/events/${eventId}/activate`),

    // Cross-references
    risks: (eventId: string) =>
      api.get<LinkedEventRisk[]>(`/events/${eventId}/risks`),
    actions: (eventId: string) =>
      api.get<LinkedEventAction[]>(`/events/${eventId}/actions`),

    // Link/unlink risks (event owns event_risk_links)
    linkRisk: (eventId: string, riskId: string) =>
      api.post<{ link_id: string }>(`/events/${eventId}/risks`, { risk_id: riskId }),
    unlinkRisk: async (eventId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/events/${eventId}/risks/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },

    // Link/unlink actions (event owns action_links where linked_to_type='event')
    linkAction: (eventId: string, actionId: string) =>
      api.post<{ link_id: string }>(`/events/${eventId}/actions`, { action_id: actionId }),
    unlinkAction: async (eventId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/events/${eventId}/actions/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },

    // Evidence
    evidence: (eventId: string, includeArchived?: boolean) =>
      api.get<EventEvidence[]>(
        `/events/${eventId}/evidence${includeArchived ? '?include_archived=true' : ''}`,
      ),
    uploadEvidence: async (eventId: string, params: { file: File; evidenceTagId: string; comment?: string }) => {
      const formData = new FormData()
      formData.append('file', params.file)
      formData.append('evidence_tag_id', params.evidenceTagId)
      if (params.comment) formData.append('comment', params.comment)
      const response = await api.fetch(`/events/${eventId}/evidence`, {
        method: 'POST',
        body: formData,
      })
      return response.json() as Promise<EventEvidence>
    },
    archiveEvidence: async (eventId: string, evidenceId: string) => {
      const response = await api.fetch(`/events/${eventId}/evidence/${evidenceId}`, {
        method: 'PATCH',
      })
      return response.json() as Promise<{ status: string }>
    },

    // Move to a different category
    move: (eventId: string, destCategoryId: string, changeNotes?: string) =>
      api.post<EventDetail>(`/events/${eventId}/move`, {
        dest_category_id: destCategoryId,
        change_notes: changeNotes ?? '',
      }),
  }
}

// --- Event type API ---

export function eventTypesApi(api: ApiClient) {
  return {
    list: () => api.get<EventType[]>('/event-types'),
  }
}
